import axios, { type AxiosResponse } from "axios";
import Log from "../../../../Logger";
import { getAccessToken } from "../../../../auth";


const GRAPHQL_API_URL = import.meta.env.VITE_GRAPHQL_API_URL ?? "";

type Input = {
    [key: string]: Input | string | number | boolean
}

type GraphQLBody = any; // resp.config.data の型
type RespBody<T extends string, U> = { data: { [key in T]: U }, errors: any }
type Resp<T extends string, U> = AxiosResponse<RespBody<T, U>, GraphQLBody>

export function mutation<T>(method: "enabledTenantFeatureSetting", inputType: "EnabledTenantFeatureSettingtInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "deleteKintoneSetting", inputType: "DeleteKintoneSettingInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "registerKintoneSetting", inputType: "RegisterKintoneSettingInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "deleteLinkServiceToken", inputType: "DeleteLinkServiceTokenInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "registerLinkServiceToken", inputType: "RegisterLinkServiceTokenInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "linkServiceTokenGeneratedFromCode", inputType: "LinkServiceTokenGeneratedFromCodeInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "registerOAuthState", inputType: "RegisterOAuthStateInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;
export function mutation<T>(method: "getOAuthStateAndDelete", inputType: "GetOAuthStateAndDeleteInput", input: Input, output: string[]): Promise<Resp<typeof method, T>>;

export async function mutation<T>(method: string, inputType: string, input: Input, output: string[]): Promise<Resp<typeof method, T>> {
    const logger = Log.getLogger();

    logger.api.info("Cognito", "Start to request: currentSession");
    const token = await getAccessToken();
    logger.api.info("Cognito", "Succeed to request: currentSession");

    let reqBody = `{"query": "mutation($input:${inputType}!) { ${method}(input:$input) { ${output.join(", ")} }}", "variables": { "input": ${JSON.stringify(input)} }}`

    if (output.length === 0) {
        reqBody = `{"query": "mutation($input:${inputType}!) { ${method}(input:$input) }", "variables": { "input": ${JSON.stringify(input)} }}`
    }

    //inputがない場合はbody構造が変わる
    if (Object.keys(input).length === 0 && input.constructor === Object) {
        reqBody = `{ "query": "mutation { ${method} { ${output.join(", ")} } }"}`
        if (output.length === 0) {
            reqBody = `{ "query": "mutation { ${method} }"}`
        }
    }

    const apiResponse = await axios.post<RespBody<typeof method, T>>(
        GRAPHQL_API_URL,
        reqBody,
        {
            headers: {
                "Content-Type": "application/graphql",
                "Authorization": `Bearer ${token}`,
            }
        }
    );
    return apiResponse;
}

export function query<T>(method: "allTenantFeatureSetting", output: string[]): Promise<Resp<typeof method, T>>;
export function query<T>(method: "isKintoneTenantSetting", output: string[]): Promise<Resp<typeof method, T>>;
export function query<T>(method: "kintoneSetting", output: string[]): Promise<Resp<typeof method, T>>;
export function query<T>(method: "linkServiceTokenExpiredDate", inputKey: "service", input: Input, output: string[]): Promise<Resp<typeof method, T>>;

export async function query<T>(method: string, inputKeyOrOutput: string | string[], input?: Input, output?: string[]): Promise<Resp<typeof method, T>> {
    if (Array.isArray(inputKeyOrOutput)) {
        output = inputKeyOrOutput;
        return query_noinput(method, output);
    }

    if (input && Array.isArray(output)) {
        const inputKey = inputKeyOrOutput;
        return query_input(method, inputKey, input, output);
    }

    throw new Error("Invalid arguments");
}

async function query_input<T>(method: string, inputKey: string, input: Input, output: string[]): Promise<Resp<typeof method, T>> {
    const logger = Log.getLogger();

    logger.api.info("Cognito", "Start to request: currentSession");
    const token = await getAccessToken();
    logger.api.info("Cognito", "Succeed to request: currentSession");

    const reqBody = `{
        "query": "query ${method}($${inputKey}: String!) { ${method}(${inputKey}: $${inputKey}) {  ${output.join(", ")} } }",
        "variables":${JSON.stringify(input)}
      }`;


    const apiResponse = await axios.post<RespBody<typeof method, T>>(
        GRAPHQL_API_URL,
        reqBody,
        {
            headers: {
                "Content-Type": "application/graphql",
                "Authorization": `Bearer ${token}`,
            }
        }
    );

    return apiResponse;
}

async function query_noinput<T>(method: string, output: string[]): Promise<Resp<typeof method, T>> {
    const logger = Log.getLogger();

    logger.api.info("Cognito", "Start to request: currentSession");
    const token = await getAccessToken();
    logger.api.info("Cognito", "Succeed to request: currentSession");

    const apiResponse = await axios.post<RespBody<typeof method, T>>(
        GRAPHQL_API_URL,
        `{"query": "query { ${method} { ${output.join(", ")} }}" }`,
        {
            headers: {
                "Content-Type": "application/graphql",
                "Authorization": `Bearer ${token}`,
            }
        }
    );

    return apiResponse;
}